import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc,
  query, where, serverTimestamp, orderBy, limit as fsLimit, startAfter
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';

// Split a letter body into recordable sentences, the way a chapter splits into
// verses. A sentence ends at . ! or ? (plus any trailing quotes/brackets);
// paragraph breaks also end a sentence even without punctuation.
export function splitIntoSentences(text) {
  if (!text) return [];
  const sentences = [];
  for (const paragraph of text.split(/\n+/)) {
    const trimmed = paragraph.trim();
    if (!trimmed) continue;
    const matches = trimmed.match(/[^.!?]+[.!?]+["'”’)\]]*|[^.!?]+$/g) || [];
    for (const match of matches) {
      const sentence = match.trim();
      if (sentence) sentences.push(sentence);
    }
  }
  return sentences;
}

function lettersCollection(familyId) {
  return collection(db, 'families', familyId, 'letters');
}

function letterRef(familyId, letterId) {
  return doc(db, 'families', familyId, 'letters', letterId);
}

function contentRef(familyId, letterId) {
  return doc(db, 'families', familyId, 'letters', letterId, 'content', 'main');
}

// Create a letter as two docs: a metadata doc (existence/subject/recipients —
// visible to the family admin) and a content subdoc (body + sentences —
// readable only by the author and recipients, per firestore.rules).
export async function createLetter(familyId, {
  authorId, authorName, subject, body, recipientIds, toEveryone, publish
}) {
  const sentences = splitIntoSentences(body);
  const metaRef = doc(lettersCollection(familyId));
  await setDoc(metaRef, {
    authorId,
    authorName,
    subject,
    recipientIds: toEveryone ? [] : recipientIds,
    toEveryone: !!toEveryone,
    status: publish ? 'published' : 'draft',
    isPublic: false,
    sentenceCount: sentences.length,
    recordedCount: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    publishedAt: publish ? serverTimestamp() : null,
  });
  await setDoc(contentRef(familyId, metaRef.id), {
    body,
    sentences,
    updatedAt: serverTimestamp(),
  });
  return metaRef.id;
}

// Author edits subject/recipients/body. Re-splits sentences; callers must
// enforce the recordedCount === 0 lock (or warn that recordings are invalidated).
export async function updateLetter(familyId, letterId, {
  subject, body, recipientIds, toEveryone
}) {
  const sentences = splitIntoSentences(body);
  await updateDoc(letterRef(familyId, letterId), {
    subject,
    recipientIds: toEveryone ? [] : recipientIds,
    toEveryone: !!toEveryone,
    sentenceCount: sentences.length,
    updatedAt: serverTimestamp(),
  });
  await setDoc(contentRef(familyId, letterId), {
    body,
    sentences,
    updatedAt: serverTimestamp(),
  });
}

// Manage-recipients: add/remove who can read a letter without touching its
// subject/body (and without re-splitting sentences, so an existing
// recording stays valid). A recipient may be added to or removed from an
// already-published letter at any time.
export async function updateLetterRecipients(familyId, letterId, { recipientIds, toEveryone }) {
  await updateDoc(letterRef(familyId, letterId), {
    recipientIds: toEveryone ? [] : recipientIds,
    toEveryone: !!toEveryone,
    updatedAt: serverTimestamp(),
  });
}

export async function publishLetter(familyId, letterId) {
  await updateDoc(letterRef(familyId, letterId), {
    status: 'published',
    publishedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

// Author or a recipient may flip a published letter public/private.
export async function setLetterPublic(familyId, letterId, isPublic) {
  await updateDoc(letterRef(familyId, letterId), {
    isPublic,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteLetter(familyId, letterId) {
  // Content first: its rules check the parent letter's authorId via get().
  await deleteDoc(contentRef(familyId, letterId));
  await deleteDoc(letterRef(familyId, letterId));
}

export async function fetchLetterContent(familyId, letterId) {
  const snap = await getDoc(contentRef(familyId, letterId));
  return snap.exists() ? snap.data() : null;
}

function snapToLetters(snap) {
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

function sortByCreatedDesc(letters) {
  return letters.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
}

// Letters the user authored (drafts included), newest first.
// Returns a plain sorted array (no Firestore cursor) — adding orderBy alongside
// a where clause requires a composite index that isn't deployed, so we sort
// client-side instead. 200-letter cap is generous for a single author.
export async function fetchLettersByAuthor(familyId, uid) {
  const snap = await getDocs(query(
    lettersCollection(familyId),
    where('authorId', '==', uid),
    fsLimit(200)
  ));
  return sortByCreatedDesc(snapToLetters(snap));
}

// Published letters the user can read: addressed to them, to everyone, or
// made public. Three queries so each is provable against the security rules.
// Cursor pagination is impractical here (three-query dedup), so we cap at 200
// per sub-query and return a plain sorted array.
export async function fetchLettersForMe(familyId, uid) {
  const col = lettersCollection(familyId);
  const [direct, everyone, publicLetters] = await Promise.all([
    getDocs(query(col, where('status', '==', 'published'), where('recipientIds', 'array-contains', uid), fsLimit(200))),
    getDocs(query(col, where('status', '==', 'published'), where('toEveryone', '==', true), fsLimit(200))),
    getDocs(query(col, where('status', '==', 'published'), where('isPublic', '==', true), fsLimit(200))),
  ]);
  const byId = new Map();
  [direct, everyone, publicLetters].forEach(snap => {
    snapToLetters(snap).forEach(letter => byId.set(letter.id, letter));
  });
  return sortByCreatedDesc([...byId.values()].filter(l => l.authorId !== uid));
}

// Admin-only: every letter in the family, newest first, with cursor pagination.
export async function fetchAllFamilyLetters(familyId, { pageSize = 10, afterDoc = null } = {}) {
  const constraints = [
    orderBy('createdAt', 'desc'),
    fsLimit(pageSize + 1),
  ];
  if (afterDoc) constraints.push(startAfter(afterDoc));
  const snap = await getDocs(query(lettersCollection(familyId), ...constraints));
  const all = snapToLetters(snap);
  const hasMore = all.length > pageSize;
  const letters = hasMore ? all.slice(0, pageSize) : all;
  const lastDoc = letters.length > 0 ? snap.docs[letters.length - 1] : null;
  return { letters, lastDoc, hasMore };
}

// ---- Letter recording ----
// ONE audio file per letter: the author reads the whole letter naturally
// (pause/continue/redo happen client-side; segments are stitched into a
// single WAV before upload). The recording doc lives under the letter (so it
// inherits letter privacy rules); the audio file lives at
// /letters/{authorId}/{letterId}/ in Storage — NOT under families/, whose
// broad storage rule would let any authed user read it. Recipients play via
// the token download URL stored on the recording doc.

function recordingsCollection(familyId, letterId) {
  return collection(db, 'families', familyId, 'letters', letterId, 'recordings');
}

export async function fetchLetterRecording(familyId, letterId) {
  const snap = await getDoc(doc(recordingsCollection(familyId, letterId), 'main'));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// Upload the assembled letter audio and mark the letter recorded.
// Re-recording overwrites the same storage path and doc. The blob is the
// browser's native recorded format (MP4/AAC or WebM/Opus) — never
// re-encoded client-side (decodeAudioData can't reliably decode iOS
// Safari's fragmented MP4, which corrupted earlier recordings).
export async function uploadLetterRecording(familyId, letterId, {
  authorId, blob, duration
}) {
  const ext = blob.type.includes('mp4') ? 'm4a' : blob.type.includes('ogg') ? 'ogg' : 'webm';
  const path = `letters/${authorId}/${letterId}/letter.${ext}`;
  const fileRef = storageRef(storage, path);
  await uploadBytes(fileRef, blob, { contentType: blob.type || 'audio/webm' });
  const audioUrl = await getDownloadURL(fileRef);

  const recRef = doc(recordingsCollection(familyId, letterId), 'main');
  const existing = await getDoc(recRef);
  // A re-recording made with a different codec (e.g. a different
  // browser/device) lands at a different extension — clean up the old file
  // so it doesn't linger in Storage.
  const oldPath = existing.exists() ? existing.data().storagePath : null;
  if (oldPath && oldPath !== path) {
    deleteObject(storageRef(storage, oldPath)).catch(() => {});
  }
  await setDoc(recRef, {
    authorId,
    audioUrl,
    duration: Math.round(duration || 0),
    storagePath: path,
    createdAt: existing.exists() ? existing.data().createdAt : serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  await updateDoc(letterRef(familyId, letterId), {
    isRecorded: true,
    recordedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { audioUrl, storagePath: path };
}

export async function deleteLetterRecording(familyId, letterId, recording) {
  try {
    await deleteObject(storageRef(storage, recording.storagePath));
  } catch (e) {
    // Missing file shouldn't block removing the doc.
    console.warn('letters: could not delete audio file', e);
  }
  await deleteDoc(doc(recordingsCollection(familyId, letterId), 'main'));
  await updateDoc(letterRef(familyId, letterId), {
    isRecorded: false,
    updatedAt: serverTimestamp(),
  });
}

// ---- Reminders (in-app nudges, stored per family) ----

function remindersCollection(familyId) {
  return collection(db, 'families', familyId, 'letterReminders');
}

// Admin nudges a recipient to read a letter (kind 'read_letter') or an
// author to publish a draft (kind 'finish_draft').
export async function sendLetterReminder(familyId, {
  letterId, letterSubject, fromUserId, fromName, toUserId, kind
}) {
  const ref = doc(remindersCollection(familyId));
  await setDoc(ref, {
    letterId,
    letterSubject: letterSubject || '',
    fromUserId,
    fromName: fromName || '',
    toUserId,
    kind: kind || 'read_letter',
    read: false,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function fetchMyUnreadReminders(familyId, uid) {
  const snap = await getDocs(query(
    remindersCollection(familyId),
    where('toUserId', '==', uid),
    where('read', '==', false)
  ));
  return sortByCreatedDesc(snap.docs.map(d => ({ id: d.id, ...d.data() })));
}

export async function markReminderRead(familyId, reminderId) {
  await updateDoc(doc(remindersCollection(familyId), reminderId), {
    read: true,
    readAt: serverTimestamp(),
  });
}
